import { useQuery } from "@tanstack/react-query";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { PrecepteePeriodItem } from "@/hooks/roster-craete/usePrecepteePeriodsQuery";

export interface NurseInboundListItem {
  /** assignment PK */
  id?: number;
  assignment_id?: number;
  status?: "active" | "cancelled" | string;
  start_date?: string;
  expected_end_date?: string | null;
  end_date?: string | null;
  startDate: string;
  endDate: string | null;
  reason: string;
  source_group_id?: string;
  office_id?: string;
  /** 파견·이동 배정 메모 */
  note?: string | null;
  /**
   * /nurses/{nurse_id}는 snake_case로 내려오며,
   * 일부 경로는 camelCase를 사용할 수 있어 둘 다 허용.
   */
  target_group_id?: string;
  target_group_name?: string;
  targetGroupId?: string;
  targetGroupName?: string;
  target_weekly_off_type?: string | null;
  target_weekly_off_enabled?: number | null;
  target_weekly_off_weekday?: number | null;
  target_shift_types?: string[] | null;
  target_team_id?: number | null;
  target_grade?: number | null;
  target_fixed_shift?: string | null;
  target_wanted_max_requests?: number | null;
}

export interface NurseInboundInfo {
  inbound_list: NurseInboundListItem[];
}

// GET /nurses/{사번} 응답 타입
export interface PreceptorPeerAssignment {
  assignment_id: number;
  start_date: string;
  expected_end_date: string | null;
  end_date: string | null;
  status: string;
  note: string | null;
}

export interface PreceptorPeer {
  nurse_id: string;
  name: string;
  /** nurse_preceptee_period(SSOT) 기간. inclusive 종료일. assignment_id 폐기. */
  start_date?: string | null;
  expected_end_date?: string | null;
}

export interface NurseDetailResponse {
  office_id: string;
  nurse_id: string;
  group_id: string;
  account_id: string;
  name: string;
  experience: number;
  role: string;
  level_: string;
  is_head_nurse: boolean;
  allowed_shifts: string[];
  personal_off_adjustment: number;
  preceptor_id?: string | null;
  exclusion_partner_id?: string | null;
  preceptees?: PreceptorPeer[];
  preceptee_period: PrecepteePeriodItem | null;
  preceptor_periods: PrecepteePeriodItem[];
  joining_date: string;
  resignation_date: string | null;
  resignation_reason: string | null;
  resignation_reason_memo: string | null;
  sequence: number;
  active: number;
  fixed_shift: string | null;
  weekly_off_weekday: number | null;
  nurse_memo: string | null;
  grade: number;
  emp_num: string;
  birth_date: string;
  phone_number: string;
  email: string | null;
  age: number;
  gender: string;
  profile_image_url: string | null;
  team_id: string | null;
  is_weekend_off: boolean;
  work_shifts: string[];
  enable_nurse_pair_preference: boolean;
  enable_aide: boolean;
  wanted_max_requests: number | null;
  show_level: boolean;
  show_preceptor: boolean;
  /** 인바운드 여부 (타 병동 파견/이동으로 유입된 간호사) */
  is_inbound?: boolean;
  /**
   * 구형: `{ inbound_list: [...] }` — 신형: 배열 그대로 `inbound: [...]`
   */
  inbound?: NurseInboundInfo | NurseInboundListItem[] | null;
}

/** 상세 API가 배열 또는 `{ inbound_list }` 둘 다 줄 수 있음 */
export function getInboundListFromDetail(detail: {
  inbound?: NurseInboundInfo | NurseInboundListItem[] | null;
}): NurseInboundListItem[] {
  const raw = detail.inbound;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw.inbound_list ?? [];
}

export function normalizeAssignmentReason(reason: string): string {
  return reason === "부서이동" ? "병동이동" : reason;
}

/**
 * 상세 API의 활성 배정 목록 (`status === "active"`).
 * 단일 소스는 `inbound`이며, `current_assignment`는 사용하지 않는다.
 */
export function getActiveAssignmentsFromDetail(detail: {
  inbound?: NurseInboundInfo | NurseInboundListItem[] | null;
}): NurseInboundListItem[] {
  const list = getInboundListFromDetail(detail);
  return list.filter((a) => a.status === "active");
}

/** @deprecated 다건 배정 지원으로 `getActiveAssignmentsFromDetail` 사용 권장 */
export function getActiveAssignmentSeed(detail: {
  inbound?: NurseInboundInfo | NurseInboundListItem[] | null;
}): NurseInboundListItem | null {
  const list = getActiveAssignmentsFromDetail(detail);
  return list[0] ?? null;
}

export function useNurseDetail(
  nurseId: string | null | undefined,
  params: {
    groupId?: string | null;
    year?: number | null;
    month?: number | null;
  } = {}
) {
  const groupId = params.groupId?.trim() ?? "";
  const year = params.year ?? null;
  const month = params.month ?? null;

  return useQuery<NurseDetailResponse>({
    queryKey: QUERY_KEYS.nurses.detail(nurseId || "", groupId, year, month),
    queryFn: async () => {
      return await httpClient.get<NurseDetailResponse>(`/nurses/${nurseId}`, {
        params: {
          ...(groupId ? { group_id: groupId } : {}),
          ...(year != null && month != null ? { year, month } : {}),
        },
      });
    },
    enabled: !!nurseId,
  });
}
